/**
 * @file routes/_app/settings/form-templates.tsx
 * @description 表單管理頁面（Task 67；2026-09-25 客戶回報序34–42 改版）。
 *
 * - 序34：名詞統一「表單管理 / 新增表單 / 編輯表單 / 表單代碼 / 表單名稱」。
 * - 序35：搜尋改由後端 search 參數過濾（舊版前端傳了 search 但後端從未處理）。
 * - 序41：真正的服務器端分頁（受控 page/pageSize，搜尋/篩選變更時回到第 1 頁），
 *        舊版未帶分頁參數只拿到後端第一頁 20 筆。
 * - 序39：列表顯示表單本身的版本（Rev）與版本日期、原始檔名、檔案上傳日期。
 * - 序42：列表顯示當前版本校對狀態；編輯表單內提供版本紀錄、逐條校對窗口與校對紀錄。
 *
 * 編輯內容見 components/form-templates/FormTemplateEditor.tsx。
 */

import { createFileRoute } from '@tanstack/react-router';
import { Button, Input, Modal, Select, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Edit3, FileText, Plus, RefreshCw, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { formatRevisionLabel } from '@zhcb/shared';
import { apiClient } from '@/lib/api-client';
import { FilterBar, ListPageShell, PageMetaItem, StatusPill } from '@/components/shell';
import { FormTemplateEditor } from '@/components/form-templates/FormTemplateEditor';
import type {
  FormTemplateListItem,
  FormTemplateListResponse,
} from '@/components/form-templates/types';
import { color, font, text, weight } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/settings/form-templates')({
  component: FormTemplatesPage,
});

/** 方案 → tone */
const schemeToneMap: Record<string, 'success' | 'brand' | 'info'> = {
  TOP: 'success',
  TAP: 'brand',
  ECO: 'info',
};

/** 搜尋輸入去抖（毫秒） */
const SEARCH_DEBOUNCE_MS = 300;

/** 表單管理頁面 */
function FormTemplatesPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<FormTemplateListItem | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filterScheme, setFilterScheme] = useState<string | undefined>();
  const [filterStatus, setFilterStatus] = useState<'active' | 'inactive' | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // 搜尋去抖：輸入停止後才送出，並回到第 1 頁（避免第 2 頁搜尋看到空結果）
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  /** 查詢表單列表（服務器端分頁 + 搜尋 + 篩選） */
  const { data, isFetching } = useQuery({
    queryKey: ['form-templates', 'list', { search, filterScheme, filterStatus, page, pageSize }],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (search) params.set('search', search);
      if (filterScheme) params.set('scheme', filterScheme);
      if (filterStatus) params.set('status', filterStatus);
      return apiClient.get<FormTemplateListResponse>(`/api/form-templates?${params.toString()}`);
    },
    placeholderData: keepPreviousData,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasFilter = !!(searchInput || filterScheme || filterStatus);

  /** 開啟新增 */
  const handleAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };

  /** 開啟編輯 */
  const handleEdit = (record: FormTemplateListItem) => {
    setEditing(record);
    setModalOpen(true);
  };

  /** 表格欄位 */
  const columns: ColumnsType<FormTemplateListItem> = [
    {
      title: '表單代碼',
      dataIndex: 'code',
      width: 170,
      render: (v: string) => (
        <span
          style={{
            fontFamily: font.mono,
            fontSize: text.sm,
            color: color.text.secondary,
            fontWeight: weight.medium,
          }}
        >
          {v}
        </span>
      ),
    },
    {
      title: '表單名稱',
      dataIndex: 'displayName',
      render: (displayName: string, record) => (
        <div>
          <div style={{ fontWeight: weight.medium, color: color.text.primary }}>{displayName}</div>
          <div style={{ fontSize: text.xs, color: color.text.tertiary }}>
            {record.name}
            {record.sourceFileName ? ` ・ 原始檔：${record.sourceFileName}` : ''}
          </div>
        </div>
      ),
    },
    {
      title: '方案',
      dataIndex: 'scheme',
      width: 80,
      render: (v: string | null) =>
        v ? (
          <StatusPill tone={schemeToneMap[v] ?? 'neutral'} size="sm">
            {v}
          </StatusPill>
        ) : (
          <StatusPill tone="muted" size="sm">
            通用
          </StatusPill>
        ),
    },
    {
      title: '版本',
      key: 'revision',
      width: 150,
      render: (_, r) =>
        r.currentVersionId ? (
          <Tooltip title={`系統版次 v${r.currentVersion ?? '?'}`}>
            <span style={{ fontFamily: font.mono, fontSize: text.sm }}>
              {formatRevisionLabel({
                revisionLabel: r.revisionLabel,
                revisionDate: r.revisionDate,
                version: r.currentVersion,
              })}
            </span>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      title: '檔案上傳日期',
      dataIndex: 'fileUploadedAt',
      width: 120,
      render: (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
    },
    {
      title: '校對',
      dataIndex: 'proofreadStatus',
      width: 90,
      render: (v: FormTemplateListItem['proofreadStatus']) =>
        v === 'confirmed' ? (
          <StatusPill tone="success" size="sm">
            已確認
          </StatusPill>
        ) : v === 'needs_fix' ? (
          <StatusPill tone="danger" size="sm">
            待修正
          </StatusPill>
        ) : (
          <StatusPill tone="muted" size="sm">
            未校對
          </StatusPill>
        ),
    },
    {
      title: '狀態',
      dataIndex: 'isActive',
      width: 80,
      render: (v: boolean) => (
        <StatusPill tone={v ? 'success' : 'muted'} size="sm" dot>
          {v ? '啟用' : '停用'}
        </StatusPill>
      ),
    },
    {
      title: '操作',
      key: '_actions',
      width: 70,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="編輯表單">
          <Button
            type="text"
            size="small"
            icon={<Edit3 size={14} />}
            onClick={() => handleEdit(record)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <ListPageShell
        title="表單管理"
        description="管理各驗證方案的表單與版本，支援上傳 Word（.docx）/ Excel（.xlsx）自動識別建立新版本，並提供逐條校對與變更發布通知。"
        meta={
          <>
            <PageMetaItem label="表單總數" value={`${total} 份`} icon={<FileText size={13} />} />
            <PageMetaItem label="啟用中" value={`${data?.activeCount ?? 0} 份`} />
          </>
        }
        actions={
          <Button type="primary" icon={<Plus size={14} />} onClick={handleAdd}>
            新增表單
          </Button>
        }
        filters={
          <FilterBar
            search={
              <Input
                placeholder="搜尋表單代碼 / 表單名稱"
                prefix={<Search size={14} color={color.text.muted} />}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                allowClear
              />
            }
            filters={
              <>
                <Select
                  placeholder="方案"
                  value={filterScheme}
                  onChange={(v) => {
                    setFilterScheme(v);
                    setPage(1);
                  }}
                  allowClear
                  style={{ width: 110 }}
                  options={[
                    { value: 'TOP', label: 'TOP' },
                    { value: 'TAP', label: 'TAP' },
                    { value: 'ECO', label: 'ECO' },
                  ]}
                />
                <Select
                  placeholder="狀態"
                  value={filterStatus}
                  onChange={(v) => {
                    setFilterStatus(v);
                    setPage(1);
                  }}
                  allowClear
                  style={{ width: 100 }}
                  options={[
                    { value: 'active', label: '啟用' },
                    { value: 'inactive', label: '停用' },
                  ]}
                />
              </>
            }
            extra={
              hasFilter && (
                <Button
                  type="text"
                  size="small"
                  icon={<RefreshCw size={13} />}
                  onClick={() => {
                    setSearchInput('');
                    setSearch('');
                    setFilterScheme(undefined);
                    setFilterStatus(undefined);
                    setPage(1);
                  }}
                >
                  重置
                </Button>
              )
            }
          />
        }
      >
        <Table<FormTemplateListItem>
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={isFetching}
          scroll={{ x: 1000 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (t) => `共 ${t} 筆資料`,
            onChange: (p, ps) => {
              if (ps !== pageSize) {
                setPageSize(ps);
                setPage(1);
              } else {
                setPage(p);
              }
            },
          }}
        />
      </ListPageShell>

      {/* 新增/編輯 Modal */}
      <Modal
        title={editing ? '編輯表單' : '新增表單'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={1000}
        destroyOnHidden
      >
        <FormTemplateEditor
          key={editing?.id ?? 'new'}
          item={editing}
          onCreated={(created) => setEditing(created)}
          onClose={() => setModalOpen(false)}
        />
      </Modal>
    </>
  );
}
